import { Fragment } from 'react';
import { NodeProps } from 'reactflow';
import * as Combobox from '@givebutter/primitives/combobox';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faHashtag } from '@fortawesome/pro-regular-svg-icons';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import {
    SupporterBirthdayTriggerFormValues,
    SupporterBirthdayTriggerNodeData,
} from 'assets/js/pages/engage/workflows/triggers/types';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import * as Form from 'components/form';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';

interface SupporterBirthdayTriggerProps extends NodeProps<SupporterBirthdayTriggerNodeData> {}

const SupporterBirthdayTrigger = ({ id, selected, data }: SupporterBirthdayTriggerProps) => {
    const { timezone } = useCurrentAccount();
    const { formData } = data;
    const { data: accountContactTags = [] } = useCurrentAccountTags();

    const methods = Form.useGivebutterForm<SupporterBirthdayTriggerFormValues>({
        mode: 'all',
        defaultValues: {
            tags: formData?.values.tags || [],
            operator: formData?.values.operator || 'any',
        },
    });
    const {
        control,
        formState: { errors },
        path,
        getValues,
        trigger,
    } = methods;
    const isError = Object.keys(errors).length > 0;

    const availableTags = sortBy(
        accountContactTags.map(tag => tag.name),
        t => t.toLowerCase(),
    );

    useSyncFormToNode<SupporterBirthdayTriggerFormValues>({ nodeId: id, control });
    useDebouncedAutosave<SupporterBirthdayTriggerFormValues>({ methods });
    useTriggerValidationOnDeselection<SupporterBirthdayTriggerFormValues>({ trigger, selected });

    const selectedTrigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY,
    );
    if (!selectedTrigger) return;

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-96"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faBolt} className="text-yellow-300" />
                            <div className="flex flex-col items-start">
                                <CanvasNode.Title>{selectedTrigger.name}</CanvasNode.Title>
                                {selected ? (
                                    <CanvasNode.Description>
                                        Configure your workflow trigger. Your workflow will initiate
                                        at 10 AM (
                                        {DateTime.now().setZone(timezone).toFormat('ZZZZ')}).
                                    </CanvasNode.Description>
                                ) : null}
                            </div>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <Form.Container>
                                    <Form.Label name={path('tags')}>Tags</Form.Label>
                                    <div className="item-center flex w-full gap-2">
                                        <Form.SingleSelect control={control} name="operator">
                                            <SingleSelect.Trigger
                                                aria-label="Operator"
                                                className="basis-max-content"
                                            />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="all">
                                                    <SingleSelect.ItemText>
                                                        All
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="any">
                                                    <SingleSelect.ItemText>
                                                        Any
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>

                                        <Form.Combobox control={control} name="tags">
                                            <Combobox.Trigger aria-label="Select tags">
                                                <Combobox.Tags placeholder="Search tags" />
                                            </Combobox.Trigger>
                                            <Combobox.Popover>
                                                <Combobox.Search />
                                                <Combobox.Items>
                                                    {availableTags.map(tag => (
                                                        <Combobox.Item value={tag} key={tag}>
                                                            <Combobox.ComboboxCheckboxIndicator />
                                                            <Combobox.ItemText>
                                                                <div className="flex items-center gap-1">
                                                                    <FontAwesomeIcon
                                                                        icon={faHashtag}
                                                                        size="xs"
                                                                        className="text-gray-400"
                                                                    />
                                                                    {tag}
                                                                </div>
                                                            </Combobox.ItemText>
                                                        </Combobox.Item>
                                                    ))}
                                                </Combobox.Items>
                                            </Combobox.Popover>
                                        </Form.Combobox>
                                    </div>
                                </Form.Container>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <SupporterBirthdayFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface SupporterBirthdayFormValuesDisplayProps {
    values: SupporterBirthdayTriggerFormValues;
    showLabel?: boolean;
}
const SupporterBirthdayFormValuesDisplay = ({
    values,
    showLabel = false,
}: SupporterBirthdayFormValuesDisplayProps) => {
    const { tags, operator } = values;
    const trigger = enabledTriggers.find(t => t.slug === WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY);
    if (!trigger) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? <p className="text-sm font-medium text-gray-900">{trigger.name}</p> : null}
            <div className="flex flex-col gap-2">
                <NodeBadges.Root>
                    {tags.map((tag, index) => (
                        <Fragment key={tag}>
                            <NodeBadges.Badge>
                                <span className="text-gray-900">{tag}</span>
                            </NodeBadges.Badge>
                            {tags.length > index + 1 ? (
                                <NodeBadges.Separator>
                                    {operator === 'any' ? 'OR' : 'AND'}
                                </NodeBadges.Separator>
                            ) : null}
                        </Fragment>
                    ))}
                </NodeBadges.Root>
            </div>
        </div>
    );
};

export { SupporterBirthdayTrigger, SupporterBirthdayFormValuesDisplay };
